import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import Sidebar from '../../components/admin/Sidebar';
import api from '../../utils/api';
import '../../styles/Admin.css';

interface Course {
  id: string;
  title: string;
  isActive: boolean;
  school: string;
}

interface Professor {
  id: string;
  username: string;
  school: string;
  role: {
    __enumName__: string;
    _code: string;
    _name: string;
  };
}

interface Class {
  id: string;
  title: string;
  status: string;
  startTime: string;
  endTime: string | null;
  school: string;
  course: Course;
  professor: Professor;
}

interface ApiResponse {
  code: number;
  message: string;
  result: Class[];
}

const AdminClasses: React.FC = () => {
  const { user, loading } = useAuth();
  const [classes, setClasses] = useState<Class[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await api.get<ApiResponse>('/classes/all');
      setClasses(response.data.result);
      setIsLoading(false);
    } catch (err) {
      setError('수업 목록을 불러오는데 실패했습니다.');
      setIsLoading(false);
    }
  };

  const handleEndClass = async (classId: string) => {
    if (!window.confirm('정말로 이 수업을 종료하시겠습니까?')) {
      return;
    }

    try {
      await api.post(`/classes/${classId}/end`);
      // 수업 목록 새로고침
      fetchClasses();
    } catch (err) {
      setError('수업 종료에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user || user.role !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="admin-container">
      <Sidebar />

      <div className="admin-content">
        <header className="admin-header">
          <div className="admin-header-content">
            <div>
              <h1 className="admin-title">수업 관리</h1>
              <p className="admin-subtitle">진행 중인 수업 목록을 확인합니다</p>
            </div>
          </div>
        </header>

        <main className="admin-main">
          {error && (
            <div className="error-message">
              <span>⚠️</span>
              {error}
            </div>
          )}

          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">진행 중인 수업</h2>
            </div>

            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <div className="admin-card-body">
                <table className="user-table">
                  <thead>
                    <tr>
                      <th>수업명</th>
                      <th>교수</th>
                      <th>학교</th>
                      <th>상태</th>
                      <th>시작 시간</th>
                      <th>작업</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map((classItem) => (
                      <tr key={classItem.id}>
                        <td>{classItem.title}</td>
                        <td>{classItem.professor.username}</td>
                        <td>{classItem.school}</td>
                        <td>
                          <span className={`badge ${classItem.status === 'IN_PROGRESS' ? 'badge-success' : 'badge-neutral'}`}>
                            {classItem.status === 'IN_PROGRESS' ? '진행중' : '대기중'}
                          </span>
                        </td>
                        <td>{new Date(classItem.startTime).toLocaleString()}</td>
                        <td>
                          {classItem.status === 'IN_PROGRESS' && (
                            <button
                              onClick={() => handleEndClass(classItem.id)}
                              className="btn btn-secondary btn-sm"
                            >
                              수업 종료
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminClasses;
